import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, MapPin, Play } from 'lucide-react';
import { EmbedFrame, Toggle, formatDuration, usePref } from './embed';
import { PLATFORM_LABEL, priceLabel, typeLabel } from './labels';
import { track as trackEvent } from './track'; // `track` is the scroll container ref below

const longDate = (iso) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} · ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};

function Slide({ listing, index, isActive, playing, muted, onPlay, register }) {
  const price = priceLabel(listing);
  const type = typeLabel(listing);
  const place = [listing.area, listing.city].filter(Boolean).join(' · ');

  return (
    <section
      ref={register}
      data-index={index}
      className="relative h-screen snap-start flex items-center justify-center md:px-4 md:pt-14 md:pb-4"
      style={{ height: '100dvh', scrollSnapStop: 'always' }}
    >
      <div className="flex gap-6 items-end h-full w-full md:w-auto justify-center">
        <div
          className="relative h-full w-full md:w-auto md:aspect-[9/16] md:rounded-xl overflow-hidden bg-black shrink-0 cursor-pointer"
          onClick={onPlay}
        >
          {playing ? (
            <EmbedFrame listing={listing} muted={muted} />
          ) : (
            <>
              {listing.thumbnail_url && (
                <img src={listing.thumbnail_url} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
              )}
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="bg-black/55 text-white rounded-full p-4"><Play size={28} fill="currentColor" /></span>
              </span>
            </>
          )}
        </div>

        <aside
          className={`absolute md:static left-0 right-0 bottom-0 md:w-80 max-h-[38%] md:max-h-full overflow-auto flex flex-col gap-2 p-4 pt-10 md:p-0 md:pb-2
            bg-gradient-to-t from-black/90 via-black/70 to-transparent md:bg-none text-stone-100 ${playing && isActive ? 'pointer-events-none md:pointer-events-auto' : ''}`}
        >
          <div className="flex items-center gap-2 font-semibold">
            <span className={`text-[11px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${listing.platform === 'tiktok' ? 'border-stone-100' : 'text-blue-300 border-blue-300'}`}>
              {PLATFORM_LABEL[listing.platform]}
            </span>
            {listing.account}
          </div>
          <div className="text-stone-400 text-sm">
            {longDate(listing.posted_at)}{listing.duration_s ? ` · ${formatDuration(listing.duration_s)}` : ''}
          </div>
          {price && <div className="text-2xl font-bold text-orange-300">{price}</div>}
          {(type || place) && (
            <div className="flex flex-wrap gap-1.5 text-xs">
              {type && <span className="bg-white/10 rounded-full px-2 py-0.5">{type}</span>}
              {place && <span className="bg-white/10 rounded-full px-2 py-0.5 inline-flex items-center gap-1"><MapPin size={12} />{place}</span>}
              {listing.furnished === 'yes' && <span className="bg-white/10 rounded-full px-2 py-0.5">Meublé</span>}
            </div>
          )}
          {listing.caption && (
            <p className="text-sm whitespace-pre-line break-words text-stone-200 line-clamp-4 md:line-clamp-none">{listing.caption}</p>
          )}
          {listing.whatsapp_url && (
            <a href={listing.whatsapp_url} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('whatsapp', listing)} className="pointer-events-auto self-start bg-green-600 hover:bg-green-700 text-white font-semibold text-sm rounded-lg px-3 py-2">
              Écrire sur WhatsApp
            </a>
          )}
          <a href={listing.url} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('contact', listing)} className="pointer-events-auto text-orange-300 font-semibold text-sm">
            Voir sur {PLATFORM_LABEL[listing.platform]} ↗
          </a>
        </aside>
      </div>
    </section>
  );
}

// TikTok-style feed: one video per screen, scroll (or arrow keys) to move, the video in view
// starts by itself. Loads the next page of listings when the end comes near.
const ReelsView = ({ listings, startIndex = 0, onClose, hasMore, onLoadMore }) => {
  const [autoplay, setAutoplay] = usePref('reelsAutoplay', true);
  const [sound, setSound] = usePref('reelsSound', true);
  const [active, setActive] = useState(startIndex);
  const played = useRef(new Set());
  const [started, setStarted] = useState(null); // slide the viewer tapped to play
  const track = useRef(null);
  const slides = useRef([]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const el = slides.current[startIndex];
    if (el && track.current) track.current.scrollTop = el.offsetTop;
    return () => { document.body.style.overflow = ''; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio >= 0.7) setActive(Number(e.target.dataset.index));
      });
    }, { root: track.current, threshold: [0.7] });
    slides.current.slice(0, listings.length).forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [listings.length]);

  useEffect(() => {
    if (hasMore && active >= listings.length - 3) onLoadMore();
  }, [active, hasMore, listings.length, onLoadMore]);

  useEffect(() => { trackEvent('reels_open'); }, []);
  useEffect(() => {
    const l = listings[active];
    if (l && (autoplay || started === active) && !played.current.has(l.id)) {
      played.current.add(l.id);
      trackEvent('play', l);
    }
  }, [active, autoplay, started, listings]);

  const step = useCallback((dir) => {
    const t = track.current;
    if (t) t.scrollBy({ top: dir * t.clientHeight, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (['ArrowDown', 'j', 'PageDown'].includes(e.key)) { e.preventDefault(); step(1); }
      if (['ArrowUp', 'k', 'PageUp'].includes(e.key)) { e.preventDefault(); step(-1); }
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [step, onClose]);

  return (
    <div role="dialog" aria-modal="true" aria-label="Mode vidéo" className="fixed inset-0 z-40 bg-neutral-950 text-stone-100">
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center gap-2 px-4 py-2.5 bg-gradient-to-b from-black/75 to-transparent">
        <button type="button" onClick={onClose} className="font-semibold bg-white/15 hover:bg-white/25 rounded-full px-3.5 py-1.5 text-sm">
          ← Annonces
        </button>
        <span className="text-stone-400 text-sm mr-auto tabular-nums">{Math.min(active + 1, listings.length)} / {listings.length}{hasMore ? '+' : ''}</span>
        <Toggle on={autoplay} onChange={setAutoplay} dark>Lecture auto</Toggle>
        <Toggle on={sound} onChange={setSound} dark>Son</Toggle>
      </div>

      <div ref={track} className="h-full overflow-y-auto snap-y snap-mandatory overscroll-contain [scrollbar-width:none]">
        {listings.map((l, i) => (
          <Slide
            key={l.id}
            listing={l}
            index={i}
            isActive={i === active}
            playing={i === active && (autoplay || started === i)}
            muted={!sound}
            onPlay={() => setStarted(i)}
            register={(el) => { slides.current[i] = el; }}
          />
        ))}
      </div>

      <div className="hidden md:flex flex-col gap-2.5 absolute right-5 top-1/2 -translate-y-1/2 z-10">
        <button type="button" aria-label="Vidéo précédente" onClick={() => step(-1)} className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><ChevronUp size={20} /></button>
        <button type="button" aria-label="Vidéo suivante" onClick={() => step(1)} className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><ChevronDown size={20} /></button>
      </div>
    </div>
  );
};

export default ReelsView;
